import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
  Button,
} from '@commenda/kakashi';
import {
  Filing,
  FilingReviewStatus,
  FilingStatusBadge,
} from '../../../molecules';

interface PastFilingsCardProps {
  pastFilings: Filing[];
  onFilingClick?: (filingId: string) => void;
  className?: string;
}

export const formatFilingPeriodForDisplay = (filingPeriod: string): string => {
  // Handle quarterly format (YYYY-Q#)
  if (filingPeriod.includes('Q')) {
    const [year, quarter] = filingPeriod.split('-Q');
    return `Q${quarter} ${year}`;
  }

  // Handle monthly format (YYYY-MM)
  if (filingPeriod.match(/^\d{4}-\d{2}$/)) {
    const [year, month] = filingPeriod.split('-');
    const date = new Date(parseInt(year), parseInt(month), 1);

    // Format as "MMM YYYY" (e.g., "Mar 2025")
    return date.toLocaleDateString('en-US', {
      month: 'short',
      year: 'numeric',
    });
  }

  // Handle annual format (just the year)
  if (filingPeriod.match(/^\d{4}$/)) {
    return filingPeriod;
  }

  // Default fallback
  return filingPeriod;
};

function PastFilingsCard({
  pastFilings = [],
  onFilingClick,
  className = '',
}: PastFilingsCardProps) {
  // show only 3 past filings
  const filingsToShow = pastFilings.slice(0, 3);

  return (
    <Accordion
      className={`max-w-[316px] w-[316px] rounded-md ${className}`}
      type="single"
      collapsible
      defaultValue="item-1"
    >
      <AccordionItem
        className="rounded-md bg-bg-secondary border border-border-primary"
        value="item-1"
      >
        <AccordionTrigger className="cursor-pointer px-xl py-xl flex justify-between">
          <p className="txt-small-medium">Past filings</p>
        </AccordionTrigger>
        <AccordionContent className="p-xl pt-0 border-t-0 flex flex-col gap-3xl">
          <div className="flex flex-col gap-lg">
            {filingsToShow.length === 0 ? (
              <div className="flex flex-col gap-lg h-[132px] justify-center items-center">
                <p className="text-text-tertiary">No past filings</p>
              </div>
            ) : (
              filingsToShow.map((filing) => (
                <div key={filing.id} className="flex items-center gap-sm">
                  <Button
                    variant="link"
                    size="sm"
                    onClick={() => onFilingClick?.(filing.id)}
                  >
                    <div className="border-b border-border-primary">
                      {filing.filingPeriod
                        ? formatFilingPeriodForDisplay(filing.filingPeriod)
                        : filing.filingName}
                    </div>
                  </Button>
                  <FilingStatusBadge
                    status={filing.filingStatus as FilingReviewStatus}
                  />
                </div>
              ))
            )}
          </div>
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  );
}

export { PastFilingsCard };
